import React, { useEffect } from 'react'
import { node, array, string, bool, object } from 'prop-types'
import { connect } from 'react-redux'
import { css } from '@emotion/react'
import { ToastWrapper } from '@livechat/design-system-react-components'

import { canUseWindow } from 'gatsby-theme-marketplace-base/src/utils/browser/window'
import {
  VIEW_MODES,
  DESIGN_SYSTEM_THEME,
} from 'gatsby-theme-marketplace-base/src/constants'
import GlobalStyles from 'gatsby-theme-marketplace-base/src/layout/global-styles'
import useHydrationContext from 'gatsby-theme-marketplace-base/src/hooks/useHydrationContext'
import { sendPostMessage } from 'gatsby-theme-marketplace-base/src/utils/postMessage/utils'
import { PM_EVENT } from 'gatsby-theme-marketplace-base/src/utils/postMessage/constants'
import AppBreadcrumbs from 'gatsby-theme-marketplace-base/src/components/Product/Breadcrumbs'
import ExpertBreadcrumbs from 'gatsby-theme-marketplace-base/src/components/Expert/Breadcrumbs'
import DeveloperBreadcrumbs from 'gatsby-theme-marketplace-base/src/components/Developer/Breadcrumbs'
import {
  containerStyles,
  mainStyles,
  mainEmbeddedStyles,
  contentStyles,
} from './layout-styles'

const customGlobalStyles = {
  "& [class^='lc-']": {
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
  },
}

const toastCss = css`
  z-index: 1100;
`

const Layout = ({
  children,
  notifications,
  app,
  expert,
  service,
  category,
  developer,
  headerLabel,
  viewMode,
  showSidebarMenu = false,
  className,
}) => {
  const hydrated = useHydrationContext()

  if (canUseWindow) {
    const LC_API = window.LC_API || {}

    LC_API.on_before_load = () => {
      LC_API.hide_chat_window()
    }
  }

  let mainStyle

  switch (viewMode) {
    case VIEW_MODES.EMBEDDED:
      mainStyle = [mainStyles(showSidebarMenu), mainEmbeddedStyles]
      break

    default:
      mainStyle = mainStyles(showSidebarMenu)
  }

  const isExpertsView =
    hydrated &&
    canUseWindow &&
    String(window.location.pathname).includes('/partners')

  const isDeveloperView =
    hydrated &&
    canUseWindow &&
    String(window.location.pathname).includes('/developers')

  useEffect(() => {
    if (app || category || service || isExpertsView || isDeveloperView) return

    sendPostMessage(PM_EVENT.BREADCRUMBS, {
      items: [
        {
          path: null,
          title: headerLabel,
        },
      ],
    })
  }, [app, category, headerLabel, isDeveloperView, isExpertsView, service])

  return (
    <>
      <ToastWrapper
        fixed={true}
        verticalPosition="top"
        horizontalPosition="center"
        toasts={notifications}
        css={toastCss}
      />
      <GlobalStyles customStyles={customGlobalStyles} />

      {/* Breadcrumbs components used only for purpose of communicating with Agent App.
            They only return the main Breadcrumbs component, which is shadowed to `null`. */}
      {app || category || service ? (
        <AppBreadcrumbs
          category={category}
          {...app}
          {...service}
          isServiceView={!!service}
        />
      ) : isExpertsView ? (
        <ExpertBreadcrumbs expert={expert} />
      ) : isDeveloperView ? (
        <DeveloperBreadcrumbs developer={developer} />
      ) : null}

      <div
        css={containerStyles}
        className={`${className} ${DESIGN_SYSTEM_THEME}`}
      >
        <main css={mainStyle}>
          <div css={contentStyles}>{children}</div>
        </main>
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    notifications: state.notifications.items,
  }
}

Layout.propTypes = {
  children: node,
  notifications: array,
  app: object,
  expert: object,
  service: object,
  category: object,
  developer: object,
  headerLabel: string,
  viewMode: string,
  showSidebarMenu: bool,
  className: string,
}

export default connect(mapStateToProps, null)(Layout)
